import React, { type ReactElement } from 'react';

import classnames from 'classnames';

import { TableExtraColumn } from '../Table.constants';
import { getColKey, getColStyle } from '../Table.helpers';
import { type ColumnConfig, type ExpandedRowRenderer, type Key } from '../Table.types';

import styles from '../Table.module.scss';

export type TableRowProps<RecordType> = {
    row: RecordType;
    rowKey: Key;
    index: number;
    rowExpandable?: (row: RecordType) => boolean;
    isExpandable?: boolean;
    isExpanded?: boolean;
    expandedRowRender?: ExpandedRowRenderer<RecordType>;
    cols: ColumnConfig<RecordType>[];
};

export const TableRow = <RecordType,>({
    row,
    rowKey,
    index,
    rowExpandable,
    isExpandable,
    isExpanded,
    expandedRowRender,
    cols
}: TableRowProps<RecordType>): ReactElement => {
    const isRowExpandable = rowExpandable ? rowExpandable(row) : isExpandable;
    return (
        <>
            <tr key={rowKey} data-key={rowKey}>
                {cols.map((column) => {
                    const columnKey = `${rowKey}-${getColKey(column)}`;
                    const value = row[column.dataIndex as never] || null;
                    return (
                        <td
                            key={columnKey}
                            className={classnames({
                                [styles.mainCol]: column.main,
                                [styles.expandButtonCol]: column.key === TableExtraColumn.Expand
                            })}
                            style={getColStyle(column)}
                        >
                            {column.render ? column.render(value, row as RecordType, index) : value}
                        </td>
                    );
                })}
            </tr>
            {isExpanded && isRowExpandable && expandedRowRender && (
                <tr key={`${rowKey}-expanded`} className={styles.expandedRow}>
                    <td colSpan={cols.length}>{expandedRowRender(row, index)}</td>
                </tr>
            )}
        </>
    );
};
